import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useMemo } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { Link } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Text from '@/components/Typography/Text';
import FlowField from '@/components/Landing/FlowField';
import LandingClosing from '@/components/Landing/LandingClosing';
import Reveal from '@/components/Landing/Reveal';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import {
  Accent,
  BleedRule,
  Chip,
  CtaRow,
  DisplayHeading,
  GhostCta,
  Kicker,
  Lede,
  Marquee,
  PrimaryCta,
  Section,
  Split,
  useEntrance,
  useInteractive,
  useLanding,
} from '@/components/Landing/LandingKit';
import {
  ACCENT,
  LAYOUT,
  MONO,
  fluid,
  isWeb,
  labelType,
} from '@/components/Landing/landingTokens';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import {
  FREE_USER_STARTING_CREDITS_DISPLAY,
  GAB_AI_SOFTWARE_ID,
  PLUS_MONTHLY_CREDITS_DISPLAY,
} from '@/constants/config';
import { getFeaturesHubSections } from '@/constants/publicNavLinks';

const SITE = 'https://gab.ai';

/* ------------------------------------------------------------------ */
/* Content                                                             */
/* ------------------------------------------------------------------ */

/** Capability names for the hero ticker — mediums, not model brands. */
const CAPABILITY_MARQUEE = [
  'Chat', 'Research', 'Images', 'Video', 'Music', 'Voice', 'Code', 'Files',
  'Documents', 'Slides', 'Spreadsheets', 'Agents', 'Tasks', 'Memory', 'API',
];

/** Output types for the Create card. Gradients are web-only; `color` is the fallback. */
const CREATE_TILES = [
  { label: 'Image', meta: '1024 × 1024', color: '#C2410C', gradient: 'linear-gradient(150deg,#F59E0B,#C2410C)' },
  { label: 'Video', meta: '0:12', color: '#2563EB', gradient: 'linear-gradient(150deg,#38BDF8,#4338CA)' },
  { label: 'Music', meta: '2:45', color: '#059669', gradient: 'linear-gradient(150deg,#34D399,#0369A1)' },
  { label: '3D', meta: '.glb', color: '#7C3AED', gradient: 'linear-gradient(150deg,#A78BFA,#DB2777)' },
];

const CREATE_CHIPS = [
  { icon: 'image', label: 'Images' },
  { icon: 'video', label: 'Video' },
  { icon: 'music', label: 'Music' },
  { icon: 'microphone', label: 'Voice' },
  { icon: 'cube', label: '3D' },
];

const AUTOMATIONS = [
  { icon: 'robot', title: 'Custom agents', meta: 'Always on' },
  { icon: 'calendar-check', title: 'Scheduled tasks', meta: 'Daily 7:00 AM' },
  { icon: 'screwdriver-wrench', title: 'Tool runs', meta: 'On demand' },
];

const DISCOVER_ROWS = [
  { icon: 'compass', label: 'Explore' },
  { icon: 'newspaper', label: 'Topics' },
  { icon: 'lightbulb', label: 'Prompt library' },
];

/** A real request against the OpenAI-compatible endpoint. */
const API_SNIPPET = [
  [{ t: 'curl', c: 'fn' }, { t: ' https://gab.ai/api/v1/chat/completions \\', c: 'p' }],
  [{ t: '  -H ', c: 'p' }, { t: '"Authorization: Bearer $GAB_API_KEY"', c: 'str' }, { t: ' \\', c: 'p' }],
  [{ t: '  -d ', c: 'p' }, { t: '\'{"model":', c: 'str' }, { t: '"arya"', c: 'kw' }, { t: ', "messages": […]}\'', c: 'str' }],
];

const CODE_COLORS = {
  kw: '#FBBF24',
  fn: '#7DD3FC',
  str: '#6EE7B7',
  p: 'rgba(247,245,241,0.66)',
};

const PILLARS = [
  {
    key: 'create',
    kicker: 'Create',
    title: 'Every medium, one prompt',
    copy: 'Describe what you want and get the artifact — a picture, a clip, a track, a voice, a model. No separate app, no separate bill.',
    href: '/images',
    cta: 'See image generation',
    wide: true,
  },
  {
    key: 'automate',
    kicker: 'Automate',
    title: 'Work that runs itself',
    copy: 'Agents you have tuned, tasks on a schedule, and tools that act.',
    href: '/agents',
    cta: 'Browse agents',
  },
  {
    key: 'discover',
    kicker: 'Discover',
    title: 'Never start from a blank page',
    copy: 'Prompts, topics, and outputs worth stealing.',
    href: '/explore',
    cta: 'Open Explore',
  },
  {
    key: 'build',
    kicker: 'Build',
    title: 'The same account, behind an API',
    copy: 'OpenAI-compatible endpoints, so anything that already speaks that dialect points at Gab AI by changing one URL.',
    href: '/api',
    cta: 'Read the API docs',
    wide: true,
    dark: true,
  },
];

/** Everything one account covers. Deliberately exhaustive — this is the page's argument. */
const INCLUDED = [
  'Chat with every major model',
  'Automatic model routing',
  'Arya, our own uncensored model',
  'Web search and deep research',
  'Image generation and editing',
  'Text-to-video and image-to-video',
  'Music and voice generation',
  '3D model generation',
  'Documents, slides, and spreadsheets',
  'File and PDF analysis',
  'Code execution and developer tools',
  'Custom agents and personas',
  'Scheduled and recurring tasks',
  'Side-by-side model comparison',
  'Memory you can edit',
  'OpenAI-compatible API access',
];

const FAQS = [
  {
    q: 'Do I need a separate subscription for images or video?',
    a: 'No. Chat, research, images, video, music, voice, code, and file analysis all run on one account and draw on one credit balance.',
  },
  {
    q: 'Which AI models can I use?',
    a: 'Flagship models from OpenAI, Anthropic, Google, open-source families, and Arya — Gab AI\'s own model. Pick one per message, or let automatic routing choose the model best suited to the request.',
  },
  {
    q: 'Are my conversations used for training?',
    a: 'Never. Your conversations, files, and creations are not used to train our model, and requests to third-party providers are anonymized so those providers never learn who is asking.',
  },
  {
    q: 'Can I bring my history from another AI service?',
    a: 'Yes. Drop in an export from ChatGPT, Claude, Grok, or another Gab AI account and every thread arrives with its title and timestamp intact, searchable alongside the rest of your work.',
  },
  {
    q: 'Is there an API?',
    a: 'Yes. Gab AI exposes OpenAI-compatible endpoints, so existing clients and editor integrations such as Cursor, Claude Code, and Codex CLI work by changing the base URL and key.',
  },
  {
    q: 'What does it cost to start?',
    a: `Nothing. A new account comes with ${FREE_USER_STARTING_CREDITS_DISPLAY} credits and no credit card. Plus adds ${PLUS_MONTHLY_CREDITS_DISPLAY} credits a month plus free messages on the default model, and you can cancel anytime.`,
  },
];

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

/**
 * Syntax-coloured source for the dark Build card.
 *
 * The lines scroll sideways rather than wrap or truncate: the endpoint URL is
 * the whole point of the snippet, and a `curl` command broken mid-word reads
 * as a rendering fault. Only the narrowest phones ever need the scroll.
 */
function CodeLines({ lines }) {
  const { isXS } = useLanding();
  const size = isXS ? 10 : 11;
  const type = { fontFamily: MONO, fontSize: size, lineHeight: size + 6 };

  return (
    <View style={isWeb ? { gap: 7, overflowX: 'auto', overflowY: 'hidden' } : { gap: 7 }}>
      {lines.map((segments, lineIndex) => (
        <Text
          key={`line-${lineIndex}`}
          style={isWeb ? { ...type, whiteSpace: 'pre' } : type}
        >
          {segments.map((segment, segmentIndex) => (
            <Text
              key={`seg-${lineIndex}-${segmentIndex}`}
              style={{ ...type, color: CODE_COLORS[segment.c] }}
            >
              {segment.t}
            </Text>
          ))}
        </Text>
      ))}
    </View>
  );
}

/** One prompt, four artifacts — the Create card's whole claim in one figure. */
function CreateFigure() {
  const { c, isXS } = useLanding();

  return (
    <View style={{ gap: 12 }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          paddingHorizontal: 14,
          paddingVertical: 12,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: c.rule,
          backgroundColor: c.panel,
        }}
      >
        <Icon name="wand-magic-sparkles" size={11} color={ACCENT} solid />
        <Text style={{ color: c.inkSoft, flex: 1, minWidth: 0, fontSize: 12.5 }} numberOfLines={1}>
          “A summer campaign for the new roaster — every asset.”
        </Text>
      </View>

      <View style={{ flexDirection: 'row', gap: isXS ? 8 : 10 }}>
        {CREATE_TILES.map((tile) => (
          <View key={tile.label} style={{ flex: 1, minWidth: 0, gap: 8 }}>
            <View
              style={{
                height: isXS ? 66 : 88,
                borderRadius: 12,
                backgroundColor: tile.color,
                ...(isWeb ? { background: tile.gradient } : {}),
              }}
            />
            <View>
              <Text weight="bold" style={{ color: c.ink, fontSize: 11.5 }} numberOfLines={1}>
                {tile.label}
              </Text>
              <Text
                style={{ color: c.faint, fontSize: 10, fontVariant: ['tabular-nums'] }}
                numberOfLines={1}
              >
                {tile.meta}
              </Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

/** Agents, tasks, and tools as a running list. */
function AutomateFigure() {
  const { c } = useLanding();

  return (
    <View style={{ gap: 9 }}>
      {AUTOMATIONS.map((row) => (
        <View
          key={row.title}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 11,
            paddingHorizontal: 13,
            paddingVertical: 12,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: c.rule,
            backgroundColor: c.panel,
          }}
        >
          <View
            style={{
              width: 28,
              height: 28,
              borderRadius: 9,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: c.accentSoft,
            }}
          >
            <Icon name={row.icon} size={11} color={ACCENT} solid />
          </View>
          <Text weight="semibold" style={{ color: c.ink, flex: 1, minWidth: 0, fontSize: 13 }}>
            {row.title}
          </Text>
          <Text style={{ color: c.faint, fontSize: 11 }} numberOfLines={1}>{row.meta}</Text>
        </View>
      ))}
    </View>
  );
}

/** A search field over the shared library. */
function DiscoverFigure() {
  const { c } = useLanding();

  return (
    <View style={{ gap: 10 }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          paddingHorizontal: 14,
          paddingVertical: 12,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: c.ruleStrong,
          backgroundColor: c.panel,
        }}
      >
        <Icon name="magnifying-glass" size={11} color={c.faint} />
        <Text style={{ color: c.faint, fontSize: 12.5 }} numberOfLines={1}>
          Search prompts, agents, topics…
        </Text>
      </View>

      {DISCOVER_ROWS.map((row) => (
        <View key={row.label} style={{ flexDirection: 'row', alignItems: 'center', gap: 11, paddingHorizontal: 4 }}>
          <Icon name={row.icon} size={12} color={ACCENT} solid />
          <Text style={{ color: c.muted, flex: 1, minWidth: 0, fontSize: 13 }}>{row.label}</Text>
          <Icon name="arrow-right" size={10} color={c.faint} />
        </View>
      ))}
    </View>
  );
}

const PILLAR_FIGURES = {
  create: CreateFigure,
  automate: AutomateFigure,
  discover: DiscoverFigure,
};

/** One capability card. The dark variant carries the code sample. */
function PillarCard({ pillar }) {
  const { c, isXS, isCompact, display } = useLanding();
  const h3 = display({ min: 22, max: 29, leading: 1.14, tracking: -0.03 });
  const Figure = PILLAR_FIGURES[pillar.key];
  const onDark = Boolean(pillar.dark);

  return (
    <View
      style={{
        flexGrow: pillar.wide ? 2 : 1,
        flexBasis: isCompact ? '100%' : (pillar.wide ? '56%' : '38%'),
        minWidth: 0,
        borderRadius: 22,
        borderWidth: 1,
        borderColor: onDark
          ? (c.isDark ? 'rgba(247,245,241,0.10)' : 'rgba(20,17,14,0.40)')
          : c.rule,
        backgroundColor: onDark ? '#0D0C0B' : c.cardSolid,
        padding: isXS ? 22 : 28,
        gap: isXS ? 22 : 28,
        justifyContent: 'space-between',
      }}
    >
      <View style={{ gap: 12 }}>
        <Text
          weight="bold"
          style={{ color: onDark ? 'rgba(247,245,241,0.5)' : c.faint, ...labelType(9.5, 0.2) }}
        >
          {pillar.kicker}
        </Text>
        <DisplayHeading
          tag="h3"
          mask={false}
          type={h3}
          color={onDark ? '#F7F5F1' : undefined}
          lines={[[pillar.title]]}
        />
        <Text
          style={{
            color: onDark ? 'rgba(247,245,241,0.58)' : c.muted,
            fontSize: 14,
            lineHeight: 22,
            maxWidth: 420,
          }}
        >
          {pillar.copy}
        </Text>
      </View>

      {onDark ? (
        <View style={{ gap: 14 }}>
          <View
            style={{
              borderRadius: 12,
              borderWidth: 1,
              borderColor: 'rgba(247,245,241,0.12)',
              backgroundColor: 'rgba(247,245,241,0.04)',
              paddingHorizontal: 14,
              paddingVertical: 14,
              overflow: 'hidden',
            }}
          >
            <CodeLines lines={API_SNIPPET} />
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
            {['OpenAI-compatible', 'Cursor', 'Claude Code', 'Codex CLI'].map((tag) => (
              <Chip key={tag} tone="onDark">{tag}</Chip>
            ))}
          </View>
        </View>
      ) : (
        <View style={{ gap: 16 }}>
          {Figure ? <Figure /> : null}
          {pillar.key === 'create' ? (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
              {CREATE_CHIPS.map((chip) => (
                <Chip key={chip.label} icon={chip.icon}>{chip.label}</Chip>
              ))}
            </View>
          ) : null}
        </View>
      )}

      <GhostCta
        label={pillar.cta}
        href={pillar.href}
        tone={onDark ? 'onDark' : undefined}
        style={{ height: 34 }}
      />
    </View>
  );
}

/**
 * One crawlable row in the directory.
 *
 * Hover state comes from `useInteractive` rather than `Pressable`'s style
 * callback: a function `style` does not survive `Link asChild`.
 */
function IndexRow({ href, label, description, isFirst }) {
  const { c } = useLanding();
  const { hovered, handlers } = useInteractive();

  return (
    <Link href={href} asChild>
      <Pressable
        {...handlers}
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 12,
          paddingVertical: 14,
          borderTopWidth: isFirst ? 0 : 1,
          borderTopColor: c.rule,
          ...(isWeb ? { cursor: 'pointer' } : {}),
        }}
      >
        <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
          <Text
            weight="semibold"
            style={{
              color: hovered ? ACCENT : c.ink,
              fontSize: 15,
              letterSpacing: -0.1,
              ...(isWeb ? { transition: 'color 160ms linear' } : {}),
            }}
          >
            {label}
          </Text>
          {description ? (
            <Text style={{ color: c.muted, fontSize: 12.5, lineHeight: 19 }}>{description}</Text>
          ) : null}
        </View>
        <View
          style={{
            paddingTop: 4,
            opacity: hovered ? 1 : 0.3,
            ...(isWeb ? {
              transform: [{ translateX: hovered ? 2 : 0 }],
              transition: 'opacity 160ms linear, transform 200ms ease',
            } : {}),
          }}
        >
          <Icon name="arrow-up-right" size={11} color={hovered ? ACCENT : c.faint} />
        </View>
      </Pressable>
    </Link>
  );
}

/** A titled column of the directory. */
function IndexGroup({ section }) {
  const { c, isCompact } = useLanding();

  return (
    <View style={{ flexGrow: 1, flexBasis: isCompact ? '100%' : 300, minWidth: 0 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingBottom: 8 }}>
        <Text tag="h3" weight="bold" style={{ color: c.ink, ...labelType(10.5, 0.18) }}>
          {section.title}
        </Text>
        <View style={{ flex: 1, height: 1, backgroundColor: c.rule }} />
      </View>
      {section.links.map((link, index) => (
        <IndexRow
          key={link.href}
          href={link.href}
          label={link.label}
          description={link.description}
          isFirst={index === 0}
        />
      ))}
    </View>
  );
}

/* ------------------------------------------------------------------ */

export default function FeaturesPage() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const insets = useSafeAreaInsets();
  const { c, band, isXS, isCompact, width, pad, display } = useLanding();
  const entered = useEntrance();

  useLandingStyles();

  const sections = useMemo(() => getFeaturesHubSections(), []);

  const headline = display({ min: 38, max: 78, leading: 0.96, tracking: -0.042 });
  const h2 = display({ min: 30, max: 52, leading: 1.02, tracking: -0.038 });

  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE}/features#page`,
        url: `${SITE}/features`,
        name: 'Gab AI features',
        description: 'Every capability in the Gab AI workspace: chat, research, images, video, music, voice, code, files, agents, scheduled tasks, and an OpenAI-compatible API.',
        isPartOf: { '@id': GAB_AI_SOFTWARE_ID },
        breadcrumb: { '@id': `${SITE}/features#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE}/features#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Gab AI', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Features', item: `${SITE}/features` },
        ],
      },
      {
        '@type': 'SoftwareApplication',
        '@id': GAB_AI_SOFTWARE_ID,
        name: 'Gab AI',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web, iOS, Android, macOS',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        featureList: INCLUDED,
      },
      {
        '@type': 'ItemList',
        '@id': `${SITE}/features#index`,
        name: 'Gab AI feature index',
        itemListElement: sections
          .flatMap((section) => section.links)
          .map((link, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: link.label,
            description: link.description,
            url: `${SITE}${link.href}`,
          })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE}/features#faq`,
        mainEntity: FAQS.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  }), [sections]);

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title="Gab AI Features — Every Kind of AI in One Workspace"
        description="Explore AI image, video, and music generation, agents, tasks, tools, API access, topics, and conservative AI — all in one private workspace."
        canonical="/features"
        keywords="Gab AI features, AI workspace, AI image generator, AI video, AI agents, AI API, uncensored AI, all-in-one AI"
        structuredData={structuredData}
      />

      <ScrollView
        {...(isWeb ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* ============ MASTHEAD ============ */}
        <View
          accessibilityRole={isWeb ? 'region' : undefined}
          accessibilityLabel="Gab AI features"
          style={{
            width: '100%',
            maxWidth: LAYOUT.max,
            paddingHorizontal: pad,
            paddingTop: (isXS ? insets.top : 0) + fluid(width, 104, 152),
            paddingBottom: fluid(width, 40, 60),
          }}
        >
          <View
            style={isWeb ? {
              opacity: entered ? 1 : 0,
              transition: 'opacity 700ms ease 120ms',
            } : null}
          >
            <Kicker>The product</Kicker>
          </View>

          <DisplayHeading
            tag="h1"
            entered={entered}
            type={headline}
            style={{ marginTop: fluid(width, 22, 32) }}
            lines={[
              ['Everything Gab AI'],
              [<Accent key="a" size={headline.fontSize} leading={headline.lineHeight}>can do</Accent>, '.'],
            ]}
          />

          <View style={{ height: 1, backgroundColor: c.rule, marginTop: fluid(width, 30, 48) }} />

          <View
            style={{
              flexDirection: isCompact ? 'column' : 'row',
              gap: isCompact ? 30 : 56,
              alignItems: isCompact ? 'stretch' : 'flex-start',
              paddingTop: fluid(width, 26, 40),
              ...(isWeb ? {
                opacity: entered ? 1 : 0,
                transform: [{ translateY: entered ? 0 : 18 }],
                transition: 'opacity 800ms ease 420ms, transform 800ms ease 420ms',
              } : {}),
            }}
          >
            <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0 }}>
              <Lede max={470}>
                Chat and research, images, video, music, voice, code, and real documents —
                with every major model and Arya, our own, in the same private workspace.
                One account, one credit balance, no add-ons.
              </Lede>
            </View>

            <View style={{ flex: isCompact ? undefined : 1, minWidth: 0, gap: 18 }}>
              <CtaRow>
                <PrimaryCta label="Start free" full={isXS} />
                <GhostCta label="See pricing" href="/pricing" />
              </CtaRow>
              <Text style={{ color: c.faint, fontSize: 12.5, letterSpacing: 0.1 }}>
                {FREE_USER_STARTING_CREDITS_DISPLAY} credits to start · No credit card required
              </Text>
            </View>
          </View>
        </View>

        <BleedRule />
        <Marquee items={CAPABILITY_MARQUEE} />
        <BleedRule />

        {/* ============ PILLARS ============ */}
        <Section label="What is inside Gab AI">
          <Reveal>
            <Kicker>The four things it does</Kicker>
            <DisplayHeading
              tag="h2"
              mask={false}
              type={h2}
              style={{ marginTop: 22 }}
              lines={[['Make it. Automate it.'], ['Find it. Build on it.']]}
            />
            <Lede max={540} style={{ marginTop: 20 }}>
              Most of these would be four separate products with four separate logins.
              Here they share one conversation, one library, and one balance.
            </Lede>
          </Reveal>

          <Reveal delay={100}>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: isXS ? 12 : 16,
                marginTop: fluid(width, 36, 56),
              }}
            >
              {PILLARS.map((pillar) => (
                <PillarCard key={pillar.key} pillar={pillar} />
              ))}
            </View>
          </Reveal>
        </Section>

        {/* ============ WHAT'S INCLUDED (band) ============ */}
        <View
          style={{
            width: '100%',
            alignSelf: 'stretch',
            alignItems: 'center',
            backgroundColor: band.bg,
            overflow: 'hidden',
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderColor: band.rule,
          }}
        >
          {isWeb ? (
            <View
              aria-hidden
              pointerEvents="none"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(ellipse 46% 62% at 84% 28%, ${band.glow}, transparent 68%)`,
              }}
            />
          ) : null}

          <Section label="What one account includes" background="transparent">
            <Split ratio={[1, 1.25]} align="flex-start">
              <Reveal>
                <Kicker tone="band">No add-ons</Kicker>
                <DisplayHeading
                  tag="h2"
                  mask={false}
                  type={h2}
                  color={band.ink}
                  style={{ marginTop: 22 }}
                  lines={[['Included,'], ['not upsold.']]}
                />
                <Lede tone="band" max={400} style={{ marginTop: 22 }}>
                  There is no creative tier, no research tier, and no per-tool pricing.
                  Everything below is on the same account from the first minute.
                </Lede>

                <View
                  style={{
                    marginTop: fluid(width, 26, 34),
                    padding: 20,
                    borderRadius: 16,
                    borderWidth: 1,
                    borderColor: band.rule,
                    backgroundColor: band.surfaceSoft,
                    gap: 14,
                  }}
                >
                  {[
                    { value: FREE_USER_STARTING_CREDITS_DISPLAY, label: 'credits when you sign up, no card' },
                    { value: PLUS_MONTHLY_CREDITS_DISPLAY, label: 'credits a month on Plus' },
                  ].map((row) => (
                    <View key={row.label} style={{ flexDirection: 'row', alignItems: 'baseline', gap: 12 }}>
                      <Text
                        weight="bold"
                        style={{
                          color: ACCENT,
                          fontSize: 26,
                          letterSpacing: -0.8,
                          fontVariant: ['tabular-nums'],
                        }}
                      >
                        {row.value}
                      </Text>
                      <Text style={{ color: band.muted, flex: 1, minWidth: 0, fontSize: 13, lineHeight: 19 }}>
                        {row.label}
                      </Text>
                    </View>
                  ))}
                  <GhostCta label="Compare plans" href="/pricing" tone="band" style={{ height: 34 }} />
                </View>
              </Reveal>

              <Reveal delay={120}>
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    borderTopWidth: 1,
                    borderTopColor: band.rule,
                  }}
                >
                  {INCLUDED.map((item) => (
                    <View
                      key={item}
                      style={{
                        flexGrow: 1,
                        flexBasis: isCompact ? '100%' : '46%',
                        minWidth: 0,
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 11,
                        paddingVertical: 13,
                        paddingRight: 16,
                        borderBottomWidth: 1,
                        borderBottomColor: band.rule,
                      }}
                    >
                      <Icon name="check" size={10} color={ACCENT} solid />
                      <Text style={{ color: band.inkSoft, flex: 1, minWidth: 0, fontSize: 13.5 }}>
                        {item}
                      </Text>
                    </View>
                  ))}
                </View>
              </Reveal>
            </Split>
          </Section>
        </View>

        {/* ============ THE INDEX ============ */}
        <Section label="Index of Gab AI features">
          <Reveal>
            <Kicker>The index</Kicker>
            <DisplayHeading
              tag="h2"
              mask={false}
              type={h2}
              style={{ marginTop: 22 }}
              lines={[['Every surface, one map.']]}
            />
            <Lede max={540} style={{ marginTop: 20 }}>
              The whole product, page by page — including the parts built for a
              particular kind of work.
            </Lede>
          </Reveal>

          <Reveal delay={80}>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                columnGap: fluid(width, 24, 48),
                rowGap: fluid(width, 30, 44),
                marginTop: fluid(width, 34, 52),
              }}
            >
              {sections.map((section) => (
                <IndexGroup key={section.title} section={section} />
              ))}
            </View>
          </Reveal>
        </Section>

        <BleedRule />

        {/* ============ FAQ ============ */}
        <Section label="Frequently asked questions">
          <Split ratio={[1, 1.5]} align="flex-start">
            <Reveal>
              <Kicker>Answers</Kicker>
              <DisplayHeading
                tag="h2"
                mask={false}
                type={h2}
                style={{ marginTop: 22 }}
                lines={[['Before you'], ['sign up.']]}
              />
              <Lede size="sm" max={340} style={{ marginTop: 20 }}>
                Still unsure about something? The docs go deeper, and support answers
                everything else.
              </Lede>
              <GhostCta label="Read the docs" href="/docs" style={{ marginTop: 12 }} />
            </Reveal>

            <Reveal delay={100}>
              <View style={{ borderTopWidth: 1, borderTopColor: c.rule }}>
                {FAQS.map((item) => (
                  <View
                    key={item.q}
                    style={{
                      paddingVertical: fluid(width, 18, 24),
                      borderBottomWidth: 1,
                      borderBottomColor: c.rule,
                      gap: 8,
                    }}
                  >
                    <Text
                      tag="h3"
                      weight="bold"
                      style={{ color: c.ink, fontSize: 16.5, letterSpacing: -0.2, lineHeight: 24 }}
                    >
                      {item.q}
                    </Text>
                    <Text tag="p" style={{ color: c.muted, fontSize: 14, lineHeight: 22, maxWidth: 620 }}>
                      {item.a}
                    </Text>
                  </View>
                ))}
              </View>
            </Reveal>
          </Split>
        </Section>

        <LandingClosing />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
